import { addSceneBackgroundDots } from '../components/background-dots';
import { addPlayerCircle } from '../components/player';
import { Colors } from '../constants/colors';
import { GAME_HEIGHT, GAME_WIDTH } from '../constants/constants';
import { Languages } from '../constants/languages';
import { Scenes } from '../constants/scenes';
import { k } from '../main';
import { getMessageForTone } from '../nls/statements';
import { GameState } from '../types/game-state';

/**
 * Intro cutscene before the crowd scene.
 * Shows the player circle and the child (same color) moving upward together.
 * After ~5 seconds the child speeds up and "runs away", transitioning to the crowd scene.
 */
export function crowdCutsceneScene() {
  scene(Scenes.CROWD_CUT, (gameState: Partial<GameState>) => {
    const SCENE_HEIGHT = GAME_HEIGHT * 4;
    const SCENE_WIDTH = GAME_WIDTH;
    debug.log('in crowd cutscene');

    const playerColorRGB = Colors[gameState.playerColor ?? 'WHITE'];

    // Base positions (bottom middle)
    const startY = SCENE_HEIGHT * 4;
    const startX = SCENE_WIDTH / 2;

    // draw dots in the background to give a sense of movement/reference
    addSceneBackgroundDots({
      sceneHeight: SCENE_HEIGHT * 4,
      sceneWidth: SCENE_WIDTH,
      spacing: 100,
    });

    // Player circle (reuse existing component so camera follows)
    const player = addPlayerCircle({
      color: rgb(...(playerColorRGB as [number, number, number])),
      pos: vec2(startX - 40, startY),
    });

    // Simple child circle (visual only for cutscene)
    const child = add([
      pos(startX + 40, startY + 4), // slight offset
      circle(20),
      { isChild: true, baseSpeed: 2, fastSpeed: 9, running: false },
    ]);

    child.onDraw(() => {
      drawCircle({ color: rgb(...(Colors.BLACK as [number, number, number])), radius: 20 });
      drawCircle({ color: rgb(...(playerColorRGB as [number, number, number])), radius: 18 });
    });

    // Keep both moving upward together until child runs
    player.onUpdate(() => {
      player.moveBy(0, -2); // steady upward movement
    });

    child.onUpdate(() => {
      const speed = child.running ? child.fastSpeed : child.baseSpeed;
      child.moveBy(0, -speed);
    });

    // At 3 seconds, have the player say the "stay near me" message in the chosen language.
    wait(3, () => {
      const language = gameState.playerKnownLanguage || Languages.ENGLISH;
      const message = getMessageForTone('stay-near-me', language);
      const textObj = add([
        k.text(message, { size: 16 }),
        pos(player.pos.x, player.pos.y - 60),
        color(0, 0, 0),
        anchor('center'),
      ]);
      wait(2, () => destroy(textObj));
    });

    // At 5 seconds, child says "No!" then accelerates away, then transition shortly after.
    wait(5, () => {
      const language = gameState.playerKnownLanguage || Languages.ENGLISH;
      const childMessage = getMessageForTone('child-no', language);
      const childText = add([
        k.text(childMessage, { size: 16 }),
        pos(child.pos.x, child.pos.y - 60),
        color(0, 0, 0),
        anchor('center'),
      ]);
      wait(2, () => destroy(childText));

      child.running = true;
      // Transition after the child has visibly accelerated.
      wait(1, () => {
        go(Scenes.CROWD, gameState);
      });
    });
  });
}
